import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { Skeleton } from "../components/Skeleton.js";
import { DownloadButton } from "../components/DownloadButton.js";

interface Connector {
  id: number;
  connectorId: number;
  status: string;
  errorCode: string | null;
}

interface Charger {
  id: number;
  cpId: string;
  vendor: string | null;
  model: string | null;
  firmware: string | null;
  location: string | null;
  nominalPowerKw: number | null;
  connected: boolean;
  lastBootAt: string | null;
  lastHeartbeatAt: string | null;
  connectors: Connector[];
}

/** Semântica visual do status OCPP em 4 cores do design system. */
type ChargerState = "online" | "charging" | "faulted" | "offline";

interface StateStyle {
  label: string;
  color: string;         // var CSS pra texto/borda/dot
  bg: string;            // fundo suave (color-mix)
  dotAnim: boolean;      // se pulsa (usado em charging + faulted)
}
function styleFor(state: ChargerState): StateStyle {
  switch (state) {
    case "charging":
      return {
        label: "Carregando",
        color: "var(--nx-orange-500)",
        bg: "color-mix(in srgb, var(--nx-orange-500) 12%, transparent)",
        dotAnim: true,
      };
    case "online":
      return {
        label: "livre",
        color: "var(--nx-success-500)",
        bg: "color-mix(in srgb, var(--nx-success-500) 12%, transparent)",
        dotAnim: false,
      };
    case "faulted":
      return {
        label: "Falha",
        color: "var(--nx-danger)",
        bg: "color-mix(in srgb, var(--nx-danger) 14%, transparent)",
        dotAnim: true,
      };
    case "offline":
      return {
        label: "Offline",
        color: "var(--nx-mute)",
        bg: "color-mix(in srgb, var(--nx-mute) 12%, transparent)",
        dotAnim: false,
      };
  }
}

/** Deriva estado do CP a partir do link WS + StatusNotification[0]. */
function deriveStationState(c: Charger): ChargerState {
  if (!c.connected) return "offline";
  const station = c.connectors.find((cn) => cn.connectorId === 0);
  if (!station) {
    // Fallback: usa status agregado dos conectores físicos
    if (c.connectors.some((cn) => cn.status === "Faulted" || cn.status === "Unavailable")) return "faulted";
    if (c.connectors.some((cn) => ["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(cn.status))) return "charging";
    return "online";
  }
  if (station.status === "Faulted" || station.status === "Unavailable") return "faulted";
  if (["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(station.status)) return "charging";
  return "online";
}

/** Estado do conector físico. */
function deriveConnectorState(status: string): ChargerState {
  if (status === "Available") return "online";
  if (["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(status)) return "charging";
  if (["Faulted","Unavailable"].includes(status)) return "faulted";
  return "offline";
}

function statusBadge(status: string) {
  const cls = ["nx-badge"];
  if (["Charging", "Preparing", "Finishing"].includes(status))
    cls.push("nx-badge--charging");
  else if (status === "Available") cls.push("nx-badge--available");
  else if (status === "Faulted" || status === "Unavailable")
    cls.push("nx-badge--fault");
  else cls.push("nx-badge--neutral");
  return <span className={cls.join(" ")}>{status}</span>;
}

/**
 * Ícone SVG do carregador rápido (poste EV com plug). Nas cores do portal —
 * a cor da carroceria segue a variável passada em `accent` pra combinar com
 * o estado (verde/laranja/vermelho/cinza). Zero hex hardcoded.
 */
function ChargerIcon({ accent }: { accent: string }) {
  return (
    <svg
      width="52"
      height="72"
      viewBox="0 0 52 72"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Ícone do carregador"
    >
      <defs>
        <filter id="cp-shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" flood-opacity="0.25" />
        </filter>
      </defs>
      <g filter="url(#cp-shadow)">
        {/* base */}
        <rect x="6" y="60" width="40" height="8" rx="2" fill="var(--nx-neutral-300)" />
        {/* corpo */}
        <rect
          x="10"
          y="10"
          width="32"
          height="52"
          rx="6"
          fill={accent}
          stroke="var(--nx-bg)"
          strokeWidth="1.5"
        />
        {/* tela */}
        <rect x="14" y="16" width="24" height="18" rx="2" fill="var(--nx-navy-900)" opacity=".85" />
        <line x1="17" y1="21" x2="35" y2="21" stroke="var(--nx-success-500)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="17" y1="25" x2="30" y2="25" stroke="var(--nx-success-500)" strokeWidth="1.2" strokeLinecap="round" opacity=".8" />
        <line x1="17" y1="29" x2="27" y2="29" stroke="var(--nx-success-500)" strokeWidth="1.2" strokeLinecap="round" opacity=".6" />
        {/* raio */}
        <path
          d="M27 40 L22 50 L26 50 L24 58 L31 46 L27 46 Z"
          fill="var(--nx-bg)"
          opacity=".95"
        />
      </g>
    </svg>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ChargersPage() {
  const { user } = useAuth();
  // null = "ainda não carregou" (renderiza skeleton); [] = carregou e não tem
  // nenhum. Sem essa distinção, o EmptyState pisca antes do primeiro fetch.
  const [chargers, setChargers] = useState<Charger[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | ChargerState>("all");
  const [locationFilter, setLocationFilter] = useState("");

  const load = useCallback(() => {
    fetch("/api/chargers", { credentials: "include" })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { chargers: Charger[] }) => setChargers(d.chargers))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const canOperate = user?.role === "admin" || user?.role === "operator";

  async function remoteStart(cpId: string) {
    setBusy(cpId + ":start");
    try {
      const res = await fetch(`/api/chargers/${cpId}/remote-start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ connectorId: 1, idTag: "CARD-DEMO-001" }),
      });
      if (!res.ok) alert(`Erro ${res.status}`);
      else load();
    } finally {
      setBusy(null);
    }
  }

  async function remoteStop(cpId: string, txId: number) {
    setBusy(cpId + ":stop");
    try {
      const res = await fetch(`/api/chargers/${cpId}/remote-stop`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactionId: txId }),
      });
      if (!res.ok) alert(`Erro ${res.status}`);
      else load();
    } finally {
      setBusy(null);
    }
  }

  async function reset(cpId: string) {
    if (!confirm(`Reiniciar ${cpId}?`)) return;
    setBusy(cpId + ":reset");
    try {
      const res = await fetch(`/api/chargers/${cpId}/reset`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "Soft" }),
      });
      if (!res.ok) alert(`Erro ${res.status}`);
      else load();
    } finally {
      setBusy(null);
    }
  }

  if (error) {
    return (
      <div className="nx-card">
        <div className="nx-error">Erro ao carregar carregadores: {error}</div>
      </div>
    );
  }

  // Subtítulo: nº total + online agora. Fica em "—" antes do primeiro fetch
  // pra não mentir "0 online" enquanto ainda carregando.
  const total = chargers?.length ?? null;
  const onlineNow = chargers?.filter((c) => c.connected).length ?? null;
  const visibleChargers = chargers?.filter((charger) => {
    const byStatus = statusFilter === "all" || deriveStationState(charger) === statusFilter;
    const byLocation = !locationFilter.trim() || (charger.location ?? "").toLocaleLowerCase("pt-BR").includes(locationFilter.trim().toLocaleLowerCase("pt-BR"));
    return byStatus && byLocation;
  });
  const sub =
    total == null
      ? "Carregando lista de carregadores…"
      : `${total} ${total === 1 ? "carregador" : "carregadores"}, ${onlineNow} online agora`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Operação</span>
          <h1>Carregadores</h1>
          <div className="nx-page-head__sub">{sub}</div>
        </div>
        <div className="nx-page-head__actions">
          {/* Export só faz sentido depois que a lista foi carregada e tem
              pelo menos um item — evita gerar XLSX vazio na 1ª renderização.
              O servidor filtra `approved` conforme 17.10; aqui não passamos
              query — pega a lista canônica. */}
          <DownloadButton
            href="/api/reports/xlsx/chargers"
            filename={`nxvolt-chargers-${new Date().toISOString().slice(0, 10)}.xlsx`}
            label="Exportar XLSX"
            variant="secondary"
            disabled={chargers == null || chargers.length === 0}
            title={
              chargers == null
                ? "Aguardando carregamento da lista"
                : chargers.length === 0
                  ? "Nenhum carregador para exportar"
                  : "Baixa a lista completa em XLSX"
            }
          />
        </div>
      </div>

      {chargers != null && chargers.length > 0 && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-4)" }}>
          <div className="nx-filters">
            <label className="nx-field"><span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | ChargerState)}>
                <option value="all">Todos</option><option value="online">Disponível</option><option value="charging">Em uso</option><option value="faulted">Falha</option><option value="offline">Fora do ar</option>
              </select>
            </label>
            <label className="nx-field"><span>Local de instalação</span>
              <input value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)} placeholder="Filtrar por local" />
            </label>
          </div>
        </div>
      )}

      {chargers != null && chargers.length === 0 && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-4)" }}>
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>
            Nenhum carregador registrado. Conecte um simulador ou charger real
            em
            <code className="mono"> ws://.../ocpp/&lt;cpId&gt;</code>.
          </p>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "1rem",
          marginTop: "var(--nx-space-4)",
        }}
      >
        {chargers == null &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={`skel-${i}`} className="nx-card" style={{ padding: "1rem" }}>
              <Skeleton variant="row" height={110} />
            </div>
          ))}
        {visibleChargers?.map((c) => {
          const state = deriveStationState(c);
          const st = styleFor(state);
          const physical = c.connectors.filter((cn) => cn.connectorId > 0);
          // Quando o CP está offline, os connector.status são o ÚLTIMO snapshot
          // antes do WebSocket cair. Não é "carregando agora" — é "estava
          // carregando quando perdemos contato". Renderizamos como cinza com
          // rótulo "último" para o operador não achar que o CP ainda opera.
          const isStale = state === "offline";
          return (
            <article
              key={c.id}
              className="nx-card"
              style={{
                padding: "1rem",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "1rem",
                borderLeft: `4px solid ${st.color}`,
                background: `linear-gradient(90deg, ${st.bg} 0%, transparent 40%), var(--nx-bg)`,
              }}
            >
              <ChargerIcon accent={st.color} />

              <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
                {/* Cabeçalho: CP ID + estado principal */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <Link
                      to={`/app/chargers/${c.cpId}`}
                      className="mono"
                      style={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color: "var(--nx-ink)",
                        textDecoration: "none",
                      }}
                    >
                      {c.cpId}
                    </Link>
                    <div
                      style={{
                        fontSize: ".72rem",
                        color: "var(--nx-mute)",
                        marginTop: 2,
                      }}
                    >
                      {[c.vendor, c.model].filter(Boolean).join(" · ") || "—"}
                      {c.firmware ? ` · fw ${c.firmware}` : ""}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}>
                      Local: {c.location ?? "não informado"}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}>
                      Potência nominal: {c.nominalPowerKw == null ? "não informada" : `${c.nominalPowerKw} kW`}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 10px",
                      borderRadius: 999,
                      background: st.bg,
                      border: `1px solid ${st.color}`,
                      color: st.color,
                      fontSize: ".8rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: st.color,
                        animation: st.dotAnim ? "nx-pulse 1.4s ease-in-out infinite" : undefined,
                      }}
                      aria-hidden="true"
                    />
                    {st.label}
                  </div>
                </div>

                {/* Conectores como bolinhas rotuladas */}
                <div>
                  <div
                    style={{
                      fontSize: ".7rem",
                      textTransform: "uppercase",
                      letterSpacing: ".05em",
                      color: "var(--nx-mute)",
                      marginBottom: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    Conectores
                    {isStale && (
                      <span
                        style={{
                          textTransform: "none",
                          fontSize: ".7rem",
                          fontWeight: 400,
                          color: "var(--nx-mute)",
                          fontStyle: "italic",
                          letterSpacing: 0,
                        }}
                        title="O CP está offline. Estes são os últimos estados reportados antes da desconexão — provavelmente não refletem a operação atual."
                      >
                        · último snapshot antes da desconexão
                      </span>
                    )}
                  </div>
                  {physical.length === 0 ? (
                    <span style={{ color: "var(--nx-mute)", fontSize: ".8rem" }}>
                      Aguardando StatusNotification
                    </span>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {physical.map((cn) => {
                        // Quando o CP está offline, força o pill do conector
                        // para a paleta cinza (offline) — o valor OCPP fica
                        // como legenda menor. Assim o operador não vê laranja
                        // "Charging" pulsante num CP que sequer responde.
                        const cst = styleFor(
                          isStale ? "offline" : deriveConnectorState(cn.status),
                        );
                        return (
                          <span
                            key={cn.id}
                            title={
                              isStale
                                ? `Último estado reportado: ${cn.status} (CP offline agora)`
                                : cn.status
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "3px 9px",
                              borderRadius: 999,
                              background: cst.bg,
                              border: `1px solid ${cst.color}`,
                              color: cst.color,
                              fontSize: ".72rem",
                              fontWeight: 600,
                              opacity: isStale ? 0.7 : 1,
                            }}
                          >
                            <strong>#{cn.connectorId}</strong>
                            <span
                              style={{
                                display: "inline-block",
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: cst.color,
                                // Nada de pulsar quando offline: sem sinal do CP,
                                // não há operação viva pra representar.
                                animation:
                                  cst.dotAnim && !isStale
                                    ? "nx-pulse 1.4s ease-in-out infinite"
                                    : undefined,
                              }}
                              aria-hidden="true"
                            />
                            {cn.status}
                            {isStale && (
                              <em
                                style={{
                                  fontStyle: "normal",
                                  fontSize: ".65rem",
                                  fontWeight: 400,
                                  color: "var(--nx-mute)",
                                }}
                              >
                                (último)
                              </em>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Rodapé: heartbeat + ações */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    borderTop: "1px solid var(--nx-line)",
                    paddingTop: 8,
                    marginTop: 4,
                  }}
                >
                  <span style={{ fontSize: ".72rem", color: "var(--nx-mute)" }}>
                    Último heartbeat: {fmt(c.lastHeartbeatAt)}
                  </span>
                  {canOperate && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        className="nx-btn nx-btn--sm"
                        onClick={() => void remoteStart(c.cpId)}
                        disabled
                        title="Start remoto bloqueado. A recarga inicia no próprio carregador."
                      >
                        Start
                      </button>
                      <button
                        type="button"
                        className="nx-btn nx-btn--sm nx-btn--ghost-dark"
                        onClick={() => void reset(c.cpId)}
                        disabled={!c.connected || busy === c.cpId + ":reset"}
                        title="Enviar Reset OCPP"
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {visibleChargers?.length === 0 && (
          <p style={{ color: "var(--nx-mute)", gridColumn: "1 / -1", textAlign: "center" }}>Nenhum carregador atende aos filtros.</p>
        )}
        {/* Keyframes inline pra não precisar mexer em global.css. */}
        <style>{`
          @keyframes nx-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: .35; transform: scale(1.6); }
          }
        `}</style>
      </div>
    </div>
  );
}

// Reuso interno da callback de stop (poderia ser exportado)
export type { Charger, Connector };
